import { entryKeys } from '@taler/api-client'
import { type CurrencyCode, type LocalDate, type Money, splitMoneyEvenly } from '@taler/domain'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, ref, type Ref, watch } from 'vue'
import { useApi } from '@/app/api'
import {
  createRow,
  createSplit,
  rowCurrency,
  setSplitTarget,
  toPostEntryInput,
  type RowDraft,
} from '../internal/draft.ts'
import { prefersReducedMotion } from '../internal/motion.ts'
import { problemDetail } from '../internal/problem.ts'
import { isSplitExpenseComplete, remainingToAllocate } from '../internal/split-expense.ts'
import type { Target } from '../internal/targets.ts'

export interface UseSplitExpenseOptions {
  readonly ledgerId: () => string | undefined
  readonly targetsById: () => ReadonlyMap<string, Target>
  readonly today: () => LocalDate
  /** The ledger membership's one payment account (myWealth §4.2) - undefined until
   * resolved, or while the current default is missing or archived (§4.8). */
  readonly defaultAccountId: () => string | undefined
}

export interface UseSplitExpense {
  readonly row: Ref<RowDraft>
  readonly payee: Ref<string>
  readonly total: Ref<Money | null>
  readonly currency: ComputedRef<CurrencyCode | undefined>
  readonly remaining: ComputedRef<Money | undefined>
  readonly canSubmit: ComputedRef<boolean>
  readonly isSubmitting: ComputedRef<boolean>
  readonly submitError: ComputedRef<Error | null>
  readonly submitErrorDetail: ComputedRef<string | undefined>
  readonly justPosted: Ref<boolean>
  setTarget(splitId: string, targetId: string | undefined): void
  addLine(): void
  removeLine(splitId: string): void
  /** Divides the declared total evenly across every current line - the captain's
   * one-click set-all (myWealth's defaults-bar `Alkalmaz mindenkire` pattern). */
  splitEvenly(): void
  /** Applies one category to every line still in category mode; a line deliberately
   * pointed at a contact is left untouched (myWealth §4.4). */
  applyCategoryToAll(categoryTargetId: string): void
  submit(): Promise<void>
  reset(): void
}

export function useSplitExpense(options: UseSplitExpenseOptions): UseSplitExpense {
  const api = useApi()
  const queryClient = useQueryClient()

  const row = ref<RowDraft>(createRow(options.today(), options.defaultAccountId())) as Ref<RowDraft>
  row.value.splits.push(createSplit())
  const payee = ref('')
  const total = ref<Money | null>(null)
  const justPosted = ref(false)
  let justPostedTimer: ReturnType<typeof setTimeout> | undefined

  // Resolves once the ledger's default payment account query lands, and follows a change
  // to it - the row never offers its own picker, so this is the only place it's set.
  watch(options.defaultAccountId, (accountId) => {
    if (accountId !== undefined) row.value.accountId = accountId
  })

  function targets(): ReadonlyMap<string, Target> {
    return options.targetsById()
  }

  const currency = computed(() => rowCurrency(row.value, targets()))
  const remaining = computed(() => remainingToAllocate(row.value, total.value, targets()))
  const canSubmit = computed(() => isSplitExpenseComplete(row.value, total.value, targets()))

  function setTarget(splitId: string, targetId: string | undefined): void {
    const split = row.value.splits.find((candidate) => candidate.id === splitId)
    if (split !== undefined) setSplitTarget(split, targetId, targets())
  }

  function addLine(): void {
    row.value.splits.push(createSplit())
  }

  function removeLine(splitId: string): void {
    if (row.value.splits.length <= 1) return
    row.value.splits = row.value.splits.filter((split) => split.id !== splitId)
  }

  function splitEvenly(): void {
    if (total.value === null || row.value.splits.length === 0) return
    const shares = splitMoneyEvenly(total.value, row.value.splits.length)
    row.value.splits.forEach((split, index) => {
      split.amount = shares[index]!
    })
  }

  function applyCategoryToAll(categoryTargetId: string): void {
    const byId = targets()
    for (const split of row.value.splits) {
      const current = split.targetId === undefined ? undefined : byId.get(split.targetId)
      if (current?.source === 'contact') continue
      setSplitTarget(split, categoryTargetId, byId)
    }
  }

  const submitMutation = useMutation({
    mutationFn: async () => {
      const input = toPostEntryInput('EXPENSE', row.value, targets())
      if (input === undefined) throw new Error('The split is not ready to post')
      const withPayee = payee.value.trim() === '' ? input : { ...input, payee: payee.value.trim() }
      return api.entries.batchCreate(options.ledgerId()!, [withPayee])
    },
    onSuccess: () => {
      justPosted.value = true
      for (const key of entryKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      if (justPostedTimer !== undefined) clearTimeout(justPostedTimer)
      justPostedTimer = setTimeout(
        () => {
          justPosted.value = false
        },
        prefersReducedMotion() ? 300 : 1000,
      )
    },
  })

  function reset(): void {
    row.value = createRow(options.today(), options.defaultAccountId())
    row.value.splits.push(createSplit())
    payee.value = ''
    total.value = null
    justPosted.value = false
    submitMutation.reset()
  }

  return {
    row,
    payee,
    total,
    currency,
    remaining,
    canSubmit,
    isSubmitting: computed(() => submitMutation.isPending.value),
    submitError: computed(() => submitMutation.error.value),
    submitErrorDetail: computed(() => problemDetail(submitMutation.error.value)),
    justPosted,
    setTarget,
    addLine,
    removeLine,
    splitEvenly,
    applyCategoryToAll,
    // The mutation's own `error` ref is what the UI reads; this promise is not awaited
    // by any caller, so a rejection here must not become an unhandled one. A split that
    // does not add up is never sent at all - not merely blocked by a disabled button.
    submit: () =>
      !canSubmit.value
        ? Promise.resolve()
        : submitMutation.mutateAsync().then(
            () => undefined,
            () => undefined,
          ),
    reset,
  }
}
